import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, MailOpen, Inbox, Star, Trash2, RefreshCw, Search, Calendar, User, Clock, CheckCircle2, Loader2, ChevronRight, Sparkles, Building2, X, FileText, Paperclip, Send, MessageCircle, Archive, Reply, AlertTriangle, Briefcase, EyeOff, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { runAiJob } from '../utils/aiJobs';
import { EmailAIDispatchDialog } from './EmailAIDispatchDialog';
import { DispatchPlansDropdown } from './DispatchPlansDropdown';
import { DocumentClassifyDialog } from './DocumentClassifyDialog';
import { NominaClassifyDialog } from './NominaClassifyDialog';
import { NominaBatchClassifyDialog } from './NominaBatchClassifyDialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const MB45_DOMAIN = 'mb45.com';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = API_URL;

// Mapeo de categorías a colores y labels
const CATEGORY_CONFIG = {
  evento_nuevo: { label: 'Evento Nuevo', color: 'bg-green-600', icon: Calendar },
  evento_cambio: { label: 'Cambio', color: 'bg-amber-600', icon: RefreshCw },
  evento_cancelacion: { label: 'Cancelación', color: 'bg-red-600', icon: X },
  importante: { label: 'Importante', color: 'bg-blue-600', icon: Star },
  cliente: { label: 'Cliente', color: 'bg-purple-600', icon: User },
  proveedor: { label: 'Proveedor', color: 'bg-cyan-600', icon: Building2 },
  newsletter: { label: 'Newsletter', color: 'bg-slate-500', icon: Mail },
  spam: { label: 'Spam', color: 'bg-slate-700', icon: Trash2 },
  otro: { label: 'Otro', color: 'bg-slate-600', icon: FileText },
};

const EmailHub = ({ onEventCreated }) => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [delicattoEmails, setDelicattoEmails] = useState([]);
  const [venueEmails, setVenueEmails] = useState([]); // Emails de todos los venues
  const [selectedVenue, setSelectedVenue] = useState(null); // Venue seleccionado para filtrar
  const [loadingVenues, setLoadingVenues] = useState(false); // Loading para venues
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailContent, setEmailContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [processingEmail, setProcessingEmail] = useState(false);
  // Dialog "Distribuir email al evento" (feb/2026)
  const [aiDispatchOpen, setAiDispatchOpen] = useState(false);
  // Dialog "Clasificar documento" (Gestión Documental Fase B, 2026-10)
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);
  const [classifyAttachment, setClassifyAttachment] = useState(null);
  // Dialog "Clasificar nómina" (Gestión Documental Fase C — mb45.com, 2026-10)
  const [nominaDialogOpen, setNominaDialogOpen] = useState(false);
  const [nominaAttachment, setNominaAttachment] = useState(null);
  // Dialog "Clasificar todos los documentos de este email" (Gestión Documental Fase E — mb45.com, 2026-10)
  const [nominaBatchDialogOpen, setNominaBatchDialogOpen] = useState(false);
  // Tab "Nóminas (mb45)"
  const [mb45Emails, setMb45Emails] = useState([]);
  const [loadingMb45, setLoadingMb45] = useState(false);
  // Remitentes ignorados (Gestión Documental Fase D) — ocultan el botón "Clasificar"
  const [remitentesIgnorados, setRemitentesIgnorados] = useState([]);
  const [showIgnoredSenders, setShowIgnoredSenders] = useState(false);
  const [ignorePopoverOpen, setIgnorePopoverOpen] = useState(false);
  const [savingIgnore, setSavingIgnore] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [filters, setFilters] = useState({
    unreadOnly: false,
    excludeNewsletters: true,
  });
  
  // Multi-cuenta: Estado para cuentas de email
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('primary');
  const [accountEmails, setAccountEmails] = useState([]);
  const [accountStats, setAccountStats] = useState(null);
  
  // Configuración de venues conocidos
  const VENUE_CONFIG = {
    delicatto: { name: 'Delicatto', color: 'bg-amber-600', hoverColor: 'hover:bg-amber-700' },
    franco_espanolas: { name: 'Franco Españolas', color: 'bg-red-700', hoverColor: 'hover:bg-red-800' },
    villa_lucia: { name: 'Villa Lucía', color: 'bg-emerald-600', hoverColor: 'hover:bg-emerald-700' },
    la_merced: { name: 'Finca San Antonio', color: 'bg-purple-600', hoverColor: 'hover:bg-purple-700' },
  };
  
  // Estados para acciones de email
  const [archivingEmail, setArchivingEmail] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAsPermanent, setDeleteAsPermanent] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sendingCustomReply, setSendingCustomReply] = useState(false);

  // Vaciar Papelera (2026-10): libera espacio borrando definitivamente todo lo que hay en Trash
  const [trashInfo, setTrashInfo] = useState(null); // {folder, count}
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);
  const [emptyingTrash, setEmptyingTrash] = useState(false);

  // Cargar datos iniciales (loadEmails se maneja en su propio useEffect reactivo a filters)
  useEffect(() => {
    loadStats();
    loadDelicattoEmails();
    loadSchedulerStatus();
    loadTelegramStatus();
    loadEmailAccounts();
    loadRemitentesIgnorados();
    loadTrashInfo();
  }, []);

  const loadTrashInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/trash/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrashInfo(response.data);
    } catch (error) {
      console.error('Error loading trash info:', error);
    }
  };

  const handleEmptyTrash = async () => {
    setEmptyingTrash(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/emails/trash/empty`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`🗑️ Papelera vaciada: ${response.data.deleted} email${response.data.deleted !== 1 ? 's' : ''} eliminado${response.data.deleted !== 1 ? 's' : ''} definitivamente`);
        setShowEmptyTrashConfirm(false);
        await loadTrashInfo();
      } else {
        toast.error(response.data.error || 'No se pudo vaciar la papelera');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vaciar la papelera');
    } finally {
      setEmptyingTrash(false);
    }
  };

  // Remitentes ignorados (Gestión Documental Fase D): nunca mostrar "Clasificar"
  const loadRemitentesIgnorados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/remitentes-ignorados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRemitentesIgnorados(response.data.remitentes || []);
    } catch (error) {
      console.error('Error loading remitentes ignorados:', error);
    }
  };

  const isIgnoredSender = (fromEmail) => {
    if (!fromEmail || remitentesIgnorados.length === 0) return false;
    const email = fromEmail.toLowerCase();
    const domain = email.split('@')[1] || '';
    return remitentesIgnorados.some((r) => {
      const v = (r.valor || '').toLowerCase();
      if (v === email) return true;
      if (!v.includes('@')) return domain === v || domain.endsWith(`.${v}`);
      return false;
    });
  };

  // Acceso rápido "Ignorar remitente" desde el email abierto (exacto o dominio completo)
  const handleIgnoreSender = async (mode) => {
    const fromEmail = (selectedEmail?.from_email || '').toLowerCase();
    if (!fromEmail) return;
    const valor = mode === 'domain' ? (fromEmail.split('@')[1] || fromEmail) : fromEmail;
    setSavingIgnore(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/remitentes-ignorados`, { valor, motivo: 'Añadido desde el inbox' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(mode === 'domain' ? `Dominio "${valor}" añadido a ignorados` : `"${valor}" añadido a ignorados`);
      setIgnorePopoverOpen(false);
      await loadRemitentesIgnorados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo añadir el remitente');
    } finally {
      setSavingIgnore(false);
    }
  };

  // Lista visible tras aplicar el filtro de remitentes ignorados (toggle "Mostrar ignorados")
  const visibleEmails = useMemo(() => {
    if (showIgnoredSenders || remitentesIgnorados.length === 0) return emails;
    return emails.filter((e) => !isIgnoredSender(e.from_email));
  }, [emails, remitentesIgnorados, showIgnoredSenders]);
  const ignoredCount = emails.length - visibleEmails.length;

  // Cargar cuentas de email configuradas
  const loadEmailAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error loading email accounts:', error);
    }
  };

  // Cargar emails de una cuenta específica
  const loadAccountEmails = async (accountId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/accounts/${accountId}/emails?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountEmails(response.data.emails || []);
      setAccountStats({
        account_name: response.data.account_name,
        account_email: response.data.account_email,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error loading account emails:', error);
      toast.error('Error al cargar emails de la cuenta');
    } finally {
      setLoading(false);
    }
  };

  // Cambiar cuenta seleccionada
  const switchAccount = (accountId) => {
    setSelectedAccount(accountId);
    loadAccountEmails(accountId);
  };

  const loadSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error loading scheduler status:', error);
    }
  };

  const loadTelegramStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramStatus(response.data);
    } catch (error) {
      console.error('Error loading telegram status:', error);
    }
  };

  const sendAutoReply = async (emailId, replyType) => {
    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/emails/auto-reply/${emailId}?reply_type=${replyType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`✉️ Respuesta enviada correctamente`);
        setSelectedEmail(null);
        setEmailContent(null);
      } else {
        toast.error('Error al enviar respuesta');
      }
    } catch (error) {
      console.error('Error sending auto-reply:', error);
      toast.error('Error al enviar respuesta');
    } finally {
      setSendingReply(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading email stats:', error);
    }
  };

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: '50',
        unread_only: filters.unreadOnly.toString(),
        exclude_newsletters: filters.excludeNewsletters.toString(),
        since_days: '60'
      });
      
      const response = await axios.get(`${API}/emails/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmails(response.data.emails || []);
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Error al cargar emails');
    } finally {
      setLoading(false);
    }
  }, [filters.unreadOnly, filters.excludeNewsletters]);

  // FIX: recargar emails cuando cambien los filtros (antes solo se cargaba en mount)
  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const loadDelicattoEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/delicatto?limit=30&since_days=60`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDelicattoEmails(response.data.emails || []);
    } catch (error) {
      console.error('Error loading Delicatto emails:', error);
    }
  };

  // Gestión Documental Fase C: emails de la asesoría mb45.com (nóminas/contratos)
  const loadMb45Emails = async () => {
    setLoadingMb45(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documentos-archivados/mb45-emails?limit=30&since_days=120`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMb45Emails(response.data.emails || []);
    } catch (error) {
      console.error('Error loading mb45 emails:', error);
      toast.error('Error al cargar emails de mb45');
    } finally {
      setLoadingMb45(false);
    }
  };

  // Cargar emails de todos los venues o de uno específico
  const loadVenueEmails = useCallback(async (venueId = null) => {
    setLoadingVenues(true);
    try {
      const token = localStorage.getItem('token');
      const url = venueId 
        ? `${API}/emails/venues?venue_id=${venueId}&limit=30&since_days=60`
        : `${API}/emails/venues?limit=50&since_days=60`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      
      setVenueEmails(response.data.emails || []);
      setSelectedVenue(venueId);
      
      // Actualizar stats si vienen en la respuesta
      if (response.data.venue_stats) {
        setStats(prev => ({
          ...prev,
          venue_breakdown: response.data.venue_stats
        }));
      }
    } catch (error) {
      console.error('[EmailHub] Error loading venue emails:', error);
      if (venueId === 'delicatto' || !venueId) {
        loadDelicattoEmails();
      }
    } finally {
      setLoadingVenues(false);
    }
  }, []);

  const checkNewEmails = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/emails/check-new`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { new_delicatto_emails, new_important_emails, action_required } = response.data;
      
      if (action_required) {
        if (new_delicatto_emails > 0) {
          toast.success(`📬 ${new_delicatto_emails} nuevo(s) email(s) de Delicatto`);
        }
        if (new_important_emails > 0) {
          toast.info(`📧 ${new_important_emails} email(s) importante(s)`);
        }
      } else {
        toast.info('No hay emails nuevos importantes');
      }
      
      // Recargar datos
      await Promise.all([loadStats(), loadEmails(), loadDelicattoEmails()]);
      
    } catch (error) {
      console.error('Error checking new emails:', error);
      toast.error('Error al verificar emails');
    } finally {
      setRefreshing(false);
    }
  };

  const openEmail = async (email) => {
    setSelectedEmail(email);
    setLoadingContent(true);
    setEmailContent(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/${email.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailContent(response.data.email);
      
      // Marcar como leído si no lo estaba
      if (!email.is_read) {
        await axios.post(`${API}/emails/${email.id}/mark-read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // FIX: actualizar TODAS las listas donde pueda estar este email
        const markRead = (e) => e.id === email.id ? { ...e, is_read: true } : e;
        setEmails(prev => prev.map(markRead));
        setDelicattoEmails(prev => prev.map(markRead));
        setVenueEmails(prev => prev.map(markRead));
        setAccountEmails(prev => prev.map(markRead));
        setSearchResults(prev => prev ? prev.map(markRead) : prev);
      }
    } catch (error) {
      console.error('Error loading email content:', error);
      toast.error('Error al cargar el email');
    } finally {
      setLoadingContent(false);
    }
  };

  const processEmailWithAI = async (emailId, isDelicatto) => {
    setProcessingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await runAiJob({
        startUrl: `${API}/emails/process/${emailId}/start?auto_create_event=${isDelicatto}`,
        statusBase: `${API}/emails/jobs`,
        body: {},
        config: { headers: { Authorization: `Bearer ${token}` } },
      });
      
      if (response.data.success) {
        const created = response.data.events_created || 0;
        const updated = response.data.events_updated || 0;
        const tasksCreated = response.data.tasks_created || 0;
        
        if (created > 0 || updated > 0) {
          const messages = [];
          if (created > 0) messages.push(`${created} creado(s)`);
          if (updated > 0) messages.push(`${updated} actualizado(s)`);
          if (tasksCreated > 0) messages.push(`${tasksCreated} reunión(es) agendada(s)`);
          toast.success(`✅ Eventos: ${messages.join(', ')}`);
          onEventCreated?.(); // Refrescar lista de eventos
        } else if (response.data.analysis) {
          // Mostrar análisis para emails no-Delicatto
          toast.info(
            <div>
              <p className="font-semibold">{response.data.analysis.categoria}</p>
              <p className="text-sm">{response.data.analysis.resumen}</p>
            </div>
          );
        } else {
          toast.info('📋 Email procesado (sin eventos nuevos)');
        }
        
        // Cerrar modal
        setSelectedEmail(null);
        setEmailContent(null);
      }
    } catch (error) {
      console.error('Error processing email:', error);
      toast.error('Error al procesar el email');
    } finally {
      setProcessingEmail(false);
    }
  };

  const searchEmails = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearchResults(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emails/search/${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.emails || []);
    } catch (error) {
      console.error('Error searching emails:', error);
      toast.error('Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  // Función para archivar email
  const archiveEmail = async (emailId) => {
    setArchivingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/emails/archive/${emailId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('📁 Email archivado correctamente');
        // FIX: limpiar de TODAS las listas (incluye mb45Emails, mismo bug que en deleteEmail)
        const filt = (e) => e.id !== emailId;
        setEmails(prev => prev.filter(filt));
        setDelicattoEmails(prev => prev.filter(filt));
        setVenueEmails(prev => prev.filter(filt));
        setAccountEmails(prev => prev.filter(filt));
        setSearchResults(prev => prev ? prev.filter(filt) : prev);
        setMb45Emails(prev => prev.filter(filt));
        setSelectedEmail(null);
        setEmailContent(null);
        // Recargar stats
        loadStats();
      } else {
        toast.error(response.data.error || 'Error al archivar');
      }
    } catch (error) {
      console.error('Error archiving email:', error);
      toast.error('Error al archivar el email');
    } finally {
      setArchivingEmail(false);
    }
  };

  // Función para eliminar email
  const deleteEmail = async (emailId, permanent = false) => {
    setDeletingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/emails/${emailId}?permanent=${permanent}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        if (permanent) {
          toast.success('🗑️ Email eliminado permanentemente');
        } else {
          toast.success('🗑️ Email movido a papelera');
        }
        // FIX: limpiar de TODAS las listas (incluye mb45Emails — bug Oct-2026:
        // faltaba aquí, por lo que un email de mb45 borrado seguía visible en
        // la pestaña "Nóminas MB45" hasta recargar la página)
        const filt = (e) => e.id !== emailId;
        setEmails(prev => prev.filter(filt));
        setDelicattoEmails(prev => prev.filter(filt));
        setVenueEmails(prev => prev.filter(filt));
        setAccountEmails(prev => prev.filter(filt));
        setSearchResults(prev => prev ? prev.filter(filt) : prev);
        setMb45Emails(prev => prev.filter(filt));
        setSelectedEmail(null);
        setEmailContent(null);
        setShowDeleteConfirm(false);
        // Recargar stats
        loadStats();
      } else {
        toast.error(response.data.error || 'Error al eliminar');
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Error al eliminar el email');
    } finally {
      setDeletingEmail(false);
    }
  };

  // Abrir modal de respuesta personalizada
  const openReplyModal = () => {
    if (emailContent) {
      setReplySubject(`RE: ${emailContent.subject || selectedEmail?.subject || ''}`);
      setReplyBody('');
      setShowReplyModal(true);
    }
  };

  // Enviar respuesta personalizada
  const sendCustomReply = async () => {
    if (!replyBody.trim()) {
      toast.error('El mensaje no puede estar vacío');
      return;
    }
    
    setSendingCustomReply(true);
    try {
      const token = localStorage.getItem('token');
      const toEmail = emailContent?.from_email || selectedEmail?.from_email;
      
      const response = await axios.post(
        `${API}/emails/reply/custom`,
        {
          to_email: toEmail,
          subject: replySubject,
          body: replyBody,
          reply_to_id: selectedEmail?.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`✉️ Respuesta enviada a ${toEmail}`);
        setShowReplyModal(false);
        setReplySubject('');
        setReplyBody('');
        // Marcar como respondido localmente
        setEmails(prev => prev.map(e => 
          e.id === selectedEmail?.id ? { ...e, replied: true } : e
        ));
        setDelicattoEmails(prev => prev.map(e => 
          e.id === selectedEmail?.id ? { ...e, replied: true } : e
        ));
      } else {
        toast.error(response.data.message || 'Error al enviar');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Error al enviar la respuesta');
    } finally {
      setSendingCustomReply(false);
    }
  };

  // Función para formatear fecha de forma inteligente
  const formatEmailDate = (dateStr) => {
    if (!dateStr) return { date: '', time: '', full: '' };
    
    // El formato viene como "2026-03-31 10:30"
    const [datePart, timePart] = dateStr.split(' ');
    if (!datePart) return { date: dateStr, time: '', full: dateStr };
    
    const emailDate = new Date(datePart + 'T' + (timePart || '00:00'));
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const emailDay = new Date(emailDate.getFullYear(), emailDate.getMonth(), emailDate.getDate());
    
    const time = timePart || '';
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    
    let dateLabel;
    if (emailDay.getTime() === today.getTime()) {
      dateLabel = 'Hoy';
    } else if (emailDay.getTime() === yesterday.getTime()) {
      dateLabel = 'Ayer';
    } else {
      const day = emailDate.getDate();
      const month = months[emailDate.getMonth()];
      dateLabel = `${day} ${month}`;
    }
    
    return {
      date: dateLabel,
      time: time,
      full: `${dateLabel} ${time}`
    };
  };

  // Renderizar un email en la lista
  const EmailRow = ({ email, onClick }) => {
    const CategoryIcon = CATEGORY_CONFIG[email.category]?.icon || Mail;
    const categoryConfig = CATEGORY_CONFIG[email.category] || CATEGORY_CONFIG.otro;
    const formattedDate = formatEmailDate(email.date);
    
    return (
      <div
        onClick={() => onClick(email)}
        className={`p-3 border-b border-slate-700/50 cursor-pointer transition-colors hover:bg-slate-800/50 ${
          !email.is_read ? 'bg-slate-800/30' : ''
        } ${email.processed ? 'border-l-2 border-l-green-500' : ''}`}
        data-testid={`email-row-${email.id}`}
      >
        <div className="flex items-start gap-2">
          {/* Indicador no leído */}
          <div className="mt-1.5 shrink-0">
            {!email.is_read ? (
              <div className="w-2 h-2 rounded-full bg-blue-500" />
            ) : email.processed ? (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            ) : (
              <div className="w-2 h-2" />
            )}
          </div>
          
          {/* Icono de categoría */}
          <div className={`p-1.5 rounded shrink-0 ${categoryConfig.color}`}>
            <CategoryIcon className="h-3.5 w-3.5 text-white" />
          </div>
          
          {/* Contenido principal */}
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Primera línea: Remitente + Fecha */}
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="flex items-center gap-2 min-w-0 overflow-hidden" style={{ maxWidth: 'calc(100% - 90px)' }}>
                <span className={`text-sm truncate ${!email.is_read ? 'font-semibold text-white' : 'text-slate-300'}`}>
                  {email.from_name || email.from_email}
                </span>
                {email.is_delicatto && (
                  <Badge className="text-[10px] bg-amber-600 px-1.5 py-0 shrink-0">Delicatto</Badge>
                )}
                {email.requiere_accion_ia && (
                  <Badge
                    className={`text-[10px] px-1.5 py-0 shrink-0 flex items-center gap-0.5 ${
                      email.urgencia_ia >= 7.5 ? 'bg-red-600' : 'bg-orange-600'
                    }`}
                    data-testid={`email-urgencia-badge-${email.id}`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {email.urgencia_ia >= 7.5 ? 'Urgente' : 'Requiere acción'}
                  </Badge>
                )}
              </div>
              {/* Fecha siempre visible - FORZAR que no se oculte */}
              <div className="text-right flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap">
                <span className={formattedDate.date === 'Hoy' ? 'text-blue-400 font-medium' : ''}>
                  {formattedDate.date}
                </span>
                <span className="text-slate-500">{formattedDate.time}</span>
              </div>
            </div>
            
            {/* Segunda línea: Asunto */}
            <p className={`text-sm truncate ${!email.is_read ? 'text-slate-200' : 'text-slate-400'}`}>
              {email.subject}
            </p>
            
            {/* Tercera línea: Badges de estado */}
            {(email.processed || email.replied) && (
              <div className="flex items-center gap-1.5 mt-1">
                {email.processed && (
                  <Badge className="text-[10px] bg-green-700 px-1.5 py-0">✓ Evento</Badge>
                )}
                {email.replied && (
                  <Badge className="text-[10px] bg-blue-700 px-1.5 py-0">✓ Respondido</Badge>
                )}
              </div>
            )}
          </div>
          
          <ChevronRight className="h-4 w-4 text-slate-600 shrink-0 mt-1" />
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-slate-900">
      {/* Header con estadísticas */}
      <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-blue-900/30 to-purple-900/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Centro de Emails</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEmptyTrashConfirm(true)}
              disabled={!trashInfo || trashInfo.count === 0}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="empty-trash-btn"
            >
              <Trash2 className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">Papelera</span>
              {trashInfo?.count > 0 && (
                <Badge className="ml-1.5 bg-red-600 hover:bg-red-600" data-testid="trash-count-badge">
                  {trashInfo.count.toLocaleString()}
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={checkNewEmails}
              disabled={refreshing}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="refresh-emails-btn"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2 hidden sm:inline">Revisar</span>
            </Button>
          </div>
        </div>
        
        {/* Stats badges - CLICKEABLES para filtrar */}
        {stats && (
          <div className="flex flex-wrap gap-2">
            <Badge 
              variant="outline" 
              className={`border-slate-600 text-slate-300 cursor-pointer hover:bg-slate-700 transition-colors ${activeTab === 'inbox' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setActiveTab('inbox')}
              data-testid="badge-inbox"
            >
              <Inbox className="h-3 w-3 mr-1" />
              {stats.total_emails.toLocaleString()}
            </Badge>
            {stats.unread_count > 0 && (
              <Badge 
                className={`bg-blue-600 cursor-pointer hover:bg-blue-700 transition-colors ${activeTab === 'unread' ? 'ring-2 ring-white' : ''}`}
                onClick={() => setActiveTab('unread')}
                data-testid="badge-unread"
              >
                {stats.unread_count} sin leer
              </Badge>
            )}
            
            {/* Badges de Venues - Dinámicos */}
            {stats.venue_breakdown && Object.entries(stats.venue_breakdown).map(([venueId, venueData]) => {
              const config = VENUE_CONFIG[venueId] || { name: venueData.name, color: 'bg-slate-600', hoverColor: 'hover:bg-slate-700' };
              return (
                <Badge 
                  key={venueId}
                  className={`${config.color} cursor-pointer ${config.hoverColor} transition-colors ${activeTab === 'venues' && selectedVenue === venueId ? 'ring-2 ring-white' : ''}`}
                  onClick={() => {
                    setActiveTab('venues');
                    loadVenueEmails(venueId);
                  }}
                  data-testid={`badge-venue-${venueId}`}
                >
                  <Building2 className="h-3 w-3 mr-1" />
                  {venueData.count} {config.name}
                </Badge>
              );
            })}
            
            {/* Fallback: Badge de Delicatto si no hay venue_breakdown */}
            {!stats.venue_breakdown && stats.delicatto_count > 0 && (
              <Badge 
                className={`bg-amber-600 cursor-pointer hover:bg-amber-700 transition-colors ${activeTab === 'delicatto' ? 'ring-2 ring-white' : ''}`}
                onClick={() => setActiveTab('delicatto')}
                data-testid="badge-delicatto"
              >
                {stats.delicatto_count} Delicatto
              </Badge>
            )}
            
            {stats.important_count > 0 && (
              <Badge 
                className={`bg-green-600 cursor-pointer hover:bg-green-700 transition-colors ${activeTab === 'important' ? 'ring-2 ring-white' : ''}`}
                onClick={() => setActiveTab('important')}
                data-testid="badge-important"
              >
                {stats.important_count} importantes
              </Badge>
            )}
            {/* Indicadores de servicios */}
            {schedulerStatus?.running && (
              <Badge variant="outline" className="border-green-600 text-green-400">
                <Clock className="h-3 w-3 mr-1" />
                Auto c/15min
              </Badge>
            )}
            {telegramStatus?.configured && (
              <Badge variant="outline" className="border-blue-500 text-blue-400">
                <MessageCircle className="h-3 w-3 mr-1" />
                Telegram
              </Badge>
            )}
            {/* Badge de planes de distribución pendientes (feb/2026) */}
            <DispatchPlansDropdown onPlanApplied={() => { loadStats?.(); }} />
          </div>
        )}
      </div>

      {/* Búsqueda */}
      <div className="p-3 border-b border-slate-700">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchEmails()}
              placeholder="Buscar por asunto o remitente..."
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              data-testid="email-search-input"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4 text-slate-500 hover:text-white" />
              </button>
            )}
          </div>
          <Button
            onClick={searchEmails}
            disabled={!searchQuery.trim() || loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="email-search-btn"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Toggle: mostrar/ocultar remitentes ignorados en los listados */}
      {remitentesIgnorados.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-3 pt-2" data-testid="toggle-ignored-senders-bar">
          <span className="text-xs text-slate-500">
            {ignoredCount > 0 && !showIgnoredSenders
              ? `${ignoredCount} email${ignoredCount !== 1 ? 's' : ''} de remitentes ignorados oculto${ignoredCount !== 1 ? 's' : ''}`
              : 'Remitentes ignorados visibles'}
          </span>
          <div className="flex items-center gap-2">
            <EyeOff className="h-3.5 w-3.5 text-slate-500" />
            <Label htmlFor="show-ignored-switch" className="text-xs text-slate-400">Mostrar ignorados</Label>
            <Switch
              id="show-ignored-switch"
              checked={showIgnoredSenders}
              onCheckedChange={setShowIgnoredSenders}
              data-testid="toggle-ignored-senders-switch"
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(newTab) => {
        setActiveTab(newTab);
        if (newTab === 'venues') {
          loadVenueEmails();
        }
        if (newTab === 'accounts' && emailAccounts.length > 0 && accountEmails.length === 0) {
          loadAccountEmails(selectedAccount);
        }
        if (newTab === 'nominas' && mb45Emails.length === 0) {
          loadMb45Emails();
        }
      }} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="flex w-full overflow-x-auto overscroll-x-contain bg-slate-800 mx-3 mt-3 rounded-lg gap-1 p-1 justify-start [&::-webkit-scrollbar]:h-1">
          <TabsTrigger value="inbox" className="shrink-0 whitespace-nowrap data-[state=active]:bg-blue-600 text-xs px-2.5">
            <Inbox className="h-4 w-4 mr-1" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="unread" className="shrink-0 whitespace-nowrap data-[state=active]:bg-sky-600 text-xs px-2.5">
            <Mail className="h-4 w-4 mr-1" />
            Sin leer
          </TabsTrigger>
          <TabsTrigger
            value="accounts"
            className="shrink-0 whitespace-nowrap data-[state=active]:bg-purple-600 text-xs px-2.5"
          >
            <User className="h-4 w-4 mr-1" />
            Cuentas
          </TabsTrigger>
          <TabsTrigger
            value="venues"
            className="shrink-0 whitespace-nowrap data-[state=active]:bg-amber-600 text-xs px-2.5"
          >
            <Building2 className="h-4 w-4 mr-1" />
            Venues
          </TabsTrigger>
          <TabsTrigger value="delicatto" className="shrink-0 whitespace-nowrap data-[state=active]:bg-amber-700 text-xs px-2.5">
            <Calendar className="h-4 w-4 mr-1" />
            Delicatto
          </TabsTrigger>
          <TabsTrigger value="important" className="shrink-0 whitespace-nowrap data-[state=active]:bg-green-600 text-xs px-2.5">
            <Star className="h-4 w-4 mr-1" />
            Importantes
          </TabsTrigger>
          <TabsTrigger value="nominas" className="shrink-0 whitespace-nowrap data-[state=active]:bg-orange-700 text-xs px-2.5" data-testid="tab-nominas-mb45">
            <Briefcase className="h-4 w-4 mr-1" />
            Nóminas
          </TabsTrigger>
        </TabsList>

        {/* Lista de emails */}
        <div className="flex-1 overflow-hidden">
          {/* Resultados de búsqueda */}
          {searchResults !== null ? (
            <ScrollArea className="h-full">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">
                    {searchResults.length} resultado(s) para "{searchQuery}"
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSearch}
                    className="text-slate-400 hover:text-white"
                  >
                    Limpiar
                  </Button>
                </div>
                {searchResults.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No se encontraron emails</p>
                  </div>
                ) : (
                  searchResults.map(email => (
                    <EmailRow key={email.id} email={email} onClick={openEmail} />
                  ))
                )}
              </div>
            </ScrollArea>
          ) : (
            <>
              {/* Tab: Inbox */}
              <TabsContent value="inbox" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                    </div>
                  ) : visibleEmails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails</p>
                    </div>
                  ) : (
                    visibleEmails.map(email => (
                      <EmailRow key={email.id} email={email} onClick={openEmail} />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Sin Leer */}
              <TabsContent value="unread" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
                    </div>
                  ) : visibleEmails.filter(e => !e.is_read).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <MailOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails sin leer</p>
                      <p className="text-xs mt-1">¡Todo al día!</p>
                    </div>
                  ) : (
                    visibleEmails
                      .filter(e => !e.is_read)
                      .map(email => (
                        <EmailRow key={email.id} email={email} onClick={openEmail} />
                      ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Cuentas de Email (Multi-cuenta) */}
              <TabsContent value="accounts" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {/* Selector de cuentas */}
                  <div className="p-3 border-b border-slate-700">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {emailAccounts.map((account) => (
                        <Badge 
                          key={account.id}
                          className={`cursor-pointer transition-colors ${
                            selectedAccount === account.id 
                              ? 'bg-purple-600 ring-2 ring-white' 
                              : 'bg-slate-700 hover:bg-purple-700'
                          }`}
                          onClick={() => switchAccount(account.id)}
                          data-testid={`account-badge-${account.id}`}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          {account.name}
                        </Badge>
                      ))}
                    </div>
                    {accountStats && (
                      <div className="text-xs text-slate-400">
                        <span className="font-medium text-slate-300">{accountStats.account_email}</span>
                        <span className="mx-2">•</span>
                        <span>{accountStats.total} emails</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Lista de emails de la cuenta seleccionada */}
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                    </div>
                  ) : accountEmails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails en esta cuenta</p>
                      <p className="text-xs mt-1">
                        {emailAccounts.length > 0 
                          ? 'Selecciona una cuenta para ver sus emails' 
                          : 'No hay cuentas configuradas'}
                      </p>
                    </div>
                  ) : (
                    accountEmails.map(email => (
                      <EmailRow key={email.id} email={email} onClick={openEmail} />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Venues (todos los espacios) */}
              <TabsContent value="venues" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {/* Filtro de venue */}
                  <div className="p-3 border-b border-slate-700 flex flex-wrap gap-2">
                    <Badge 
                      className={`cursor-pointer transition-colors ${!selectedVenue ? 'bg-slate-600 ring-2 ring-white' : 'bg-slate-700 hover:bg-slate-600'}`}
                      onClick={() => loadVenueEmails(null)}
                    >
                      Todos
                    </Badge>
                    {Object.entries(VENUE_CONFIG).map(([venueId, config]) => (
                      <Badge 
                        key={venueId}
                        className={`cursor-pointer transition-colors ${selectedVenue === venueId ? `${config.color} ring-2 ring-white` : `bg-slate-700 ${config.hoverColor}`}`}
                        onClick={() => loadVenueEmails(venueId)}
                      >
                        {config.name}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Lista de emails de venues */}
                  {loadingVenues ? (
                    <div className="text-center py-8 text-slate-400" data-testid="venues-loading">
                      <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-amber-500" />
                      <p>Cargando emails de venues...</p>
                      <p className="text-xs mt-1 text-slate-500">Conectando con el servidor de correo</p>
                    </div>
                  ) : venueEmails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails de venues</p>
                      <p className="text-xs mt-1">Cuando recibas emails de Delicatto, Franco Españolas, Villa Lucía o Finca San Antonio aparecerán aquí</p>
                    </div>
                  ) : (
                    venueEmails.map(email => (
                      <div key={email.id} className="relative">
                        {/* Badge del venue */}
                        {email.venue_name && (
                          <div className="absolute top-2 right-2 z-10">
                            <Badge className={`text-[10px] ${VENUE_CONFIG[email.venue_id]?.color || 'bg-slate-600'}`}>
                              {email.venue_name}
                            </Badge>
                          </div>
                        )}
                        <EmailRow email={email} onClick={openEmail} />
                      </div>
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Delicatto */}
              <TabsContent value="delicatto" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {delicattoEmails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails de Delicatto recientes</p>
                    </div>
                  ) : (
                    delicattoEmails.map(email => (
                      <EmailRow key={email.id} email={email} onClick={openEmail} />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Importantes */}
              <TabsContent value="important" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {visibleEmails.filter(e => e.priority <= 2).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails importantes</p>
                    </div>
                  ) : (
                    visibleEmails
                      .filter(e => e.priority <= 2)
                      .map(email => (
                        <EmailRow key={email.id} email={email} onClick={openEmail} />
                      ))
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Tab: Nóminas (mb45) — Gestión Documental Fase C */}
              <TabsContent value="nominas" className="h-full m-0 data-[state=inactive]:hidden">
                <ScrollArea className="h-full">
                  {loadingMb45 ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                    </div>
                  ) : mb45Emails.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay emails de la asesoría (mb45.com)</p>
                    </div>
                  ) : (
                    mb45Emails.map(email => (
                      <EmailRow key={email.id} email={email} onClick={openEmail} />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>
            </>
          )}
        </div>
      </Tabs>

      {/* Modal de contenido de email */}
      <Dialog open={!!selectedEmail} onOpenChange={() => { setSelectedEmail(null); setEmailContent(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {selectedEmail?.is_delicatto && (
                <Badge className="bg-amber-600">Delicatto</Badge>
              )}
              <span className="truncate">{selectedEmail?.subject}</span>
            </DialogTitle>
            <DialogDescription className="text-slate-400 flex items-center gap-2 flex-wrap">
              <span>De: {selectedEmail?.from_name || selectedEmail?.from_email}</span>
              {!isIgnoredSender(selectedEmail?.from_email) && (
                <Popover open={ignorePopoverOpen} onOpenChange={setIgnorePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 px-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-900/20"
                      data-testid="quick-ignore-sender-btn"
                    >
                      <EyeOff className="h-3 w-3 mr-1" />
                      Ignorar remitente
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 bg-slate-800 border-slate-700 text-white" data-testid="quick-ignore-sender-popover">
                    <p className="text-xs text-slate-400 mb-3">
                      ¿Qué quieres ignorar? No volverá a aparecer en el listado del inbox ni ofrecerá "Clasificar".
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        disabled={savingIgnore}
                        onClick={() => handleIgnoreSender('exact')}
                        className="bg-slate-700 hover:bg-slate-600 justify-start"
                        data-testid="quick-ignore-exact-btn"
                      >
                        Solo esta dirección: {selectedEmail?.from_email}
                      </Button>
                      <Button
                        size="sm"
                        disabled={savingIgnore}
                        onClick={() => handleIgnoreSender('domain')}
                        className="bg-rose-700 hover:bg-rose-600 justify-start"
                        data-testid="quick-ignore-domain-btn"
                      >
                        Todo el dominio: @{(selectedEmail?.from_email || '').split('@')[1]}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatEmailDate(selectedEmail?.date).date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatEmailDate(selectedEmail?.date).time}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {loadingContent ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
              </div>
            ) : emailContent ? (
              <div className="space-y-4">
                {/* Adjuntos */}
                {emailContent.attachments?.length > 0 && (
                  <>
                    {(selectedEmail?.from_email || '').toLowerCase().includes(MB45_DOMAIN) &&
                      !isIgnoredSender(selectedEmail?.from_email) &&
                      emailContent.attachments.length > 1 && (
                        <Button
                          size="sm"
                          onClick={() => setNominaBatchDialogOpen(true)}
                          className="bg-amber-600 hover:bg-amber-500 w-full justify-center"
                          data-testid="classify-all-attachments-btn"
                        >
                          <Layers className="h-4 w-4 mr-2" />
                          Clasificar todos los documentos de este email ({emailContent.attachments.length})
                        </Button>
                      )}
                    <div className="flex flex-wrap gap-2">
                      {emailContent.attachments.map((att, idx) => {
                        const isMb45 = (selectedEmail?.from_email || '').toLowerCase().includes(MB45_DOMAIN);
                        const ignored = isIgnoredSender(selectedEmail?.from_email);
                        return (
                          <div key={idx} className="flex items-center gap-1.5 bg-slate-800 border border-slate-600 rounded-full pl-2.5 pr-1 py-1">
                            <Paperclip className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="text-xs text-slate-300 truncate max-w-[140px]">{att.filename}</span>
                            {ignored ? (
                              <span className="text-xs text-slate-500 px-2" data-testid={`ignored-sender-label-${idx}`}>remitente ignorado</span>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className={isMb45 ? "h-6 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30" : "h-6 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30"}
                                onClick={() => {
                                  if (isMb45) {
                                    setNominaAttachment(att);
                                    setNominaDialogOpen(true);
                                  } else {
                                    setClassifyAttachment(att);
                                    setClassifyDialogOpen(true);
                                  }
                                }}
                                aria-label={isMb45 ? 'Clasificar nómina' : 'Clasificar adjunto'}
                                data-testid={`classify-attachment-btn-${idx}`}
                              >
                                {isMb45 ? 'Clasificar nómina' : 'Clasificar'}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                
                {/* Contenido */}
                <div className="bg-slate-800 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans">
                    {emailContent.body_text || 'Sin contenido de texto'}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No se pudo cargar el contenido</p>
            )}
          </div>

          {/* Acciones */}
          <div className="shrink-0 pt-4 border-t border-slate-700 space-y-2 safe-bottom">
            {/* Fila 1: Procesar con IA — flex-wrap evita desbordamiento en móvil */}
            <div className="flex flex-wrap gap-2">
              {selectedEmail?.is_delicatto && (
                <Button
                  onClick={() => processEmailWithAI(selectedEmail.id, true)}
                  disabled={processingEmail}
                  className="flex-1 min-w-[140px] bg-amber-600 hover:bg-amber-700"
                  data-testid="process-delicatto-btn"
                >
                  {processingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  <span className="truncate">{selectedEmail?.processed ? 'Crear de nuevo' : 'Crear Evento(s)'}</span>
                </Button>
              )}
              <Button
                onClick={() => processEmailWithAI(selectedEmail?.id, false)}
                disabled={processingEmail}
                variant="outline"
                className="flex-1 min-w-[120px] border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="analyze-email-btn"
              >
                {processingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                <span className="truncate">Analizar<span className="hidden sm:inline"> con IA</span></span>
              </Button>
              <Button
                onClick={() => setAiDispatchOpen(true)}
                variant="outline"
                className="flex-1 min-w-[120px] border-cyan-600 text-cyan-300 hover:bg-cyan-900/30"
                data-testid="ai-dispatch-open-btn"
                title="Extrae novios/fecha, peticiones (YouTube) y documentos (PDF) del email y los distribuye al evento correspondiente"
              >
                <Sparkles className="h-4 w-4 mr-1 text-amber-400 shrink-0" />
                <span className="truncate">Distribuir<span className="hidden sm:inline"> al evento</span></span>
              </Button>
            </div>
            
            {/* Fila 2: Responder */}
            <div className="flex gap-2">
              {/* Botón responder personalizado - siempre visible */}
              <Button
                onClick={openReplyModal}
                variant="outline"
                className="flex-1 border-blue-600 text-blue-400 hover:bg-blue-900/30"
                data-testid="reply-custom-btn"
              >
                <Reply className="h-4 w-4 mr-2" />
                Responder
              </Button>
              
              {/* Respuestas automáticas según tipo */}
              {selectedEmail?.is_venue && !selectedEmail?.replied && (
                <Button
                  onClick={() => sendAutoReply(selectedEmail.id, 'venue')}
                  disabled={sendingReply}
                  variant="outline"
                  className="flex-1 border-green-700 text-green-400 hover:bg-green-900/30"
                  data-testid="reply-venue-btn"
                >
                  {sendingReply ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Confirmar recepción
                </Button>
              )}
              {selectedEmail?.category === 'cliente' && !selectedEmail?.replied && !selectedEmail?.is_venue && (
                <Button
                  onClick={() => sendAutoReply(selectedEmail.id, 'client')}
                  disabled={sendingReply}
                  variant="outline"
                  className="flex-1 border-purple-700 text-purple-400 hover:bg-purple-900/30"
                  data-testid="reply-client-btn"
                >
                  {sendingReply ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Auto-respuesta
                </Button>
              )}
            </div>

            {/* Fila 3: Archivar y Eliminar */}
            <div className="flex gap-2">
              <Button
                onClick={() => archiveEmail(selectedEmail?.id)}
                disabled={archivingEmail}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="archive-email-btn"
              >
                {archivingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Archive className="h-4 w-4 mr-2" />
                )}
                Archivar
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deletingEmail}
                variant="outline"
                className="flex-1 border-red-700 text-red-400 hover:bg-red-900/30"
                data-testid="delete-email-btn"
              >
                {deletingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Eliminar
              </Button>
            </div>

            {/* Indicadores de estado */}
            {selectedEmail?.replied && (
              <div className="flex items-center gap-2 p-2 bg-blue-900/20 border border-blue-700/50 rounded-md">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                <span className="text-blue-400 text-sm">Este email ya fue respondido</span>
              </div>
            )}
            {selectedEmail?.processed && (
              <div className="flex items-center gap-2 p-2 bg-green-900/20 border border-green-700/50 rounded-md">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-green-400 text-sm">Este email ya tiene un evento creado</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Eliminar Email
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Qué quieres hacer con este email?
              <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-300 truncate">
                  <strong>De:</strong> {selectedEmail?.from_name || selectedEmail?.from_email}
                </p>
                <p className="text-sm text-slate-300 truncate">
                  <strong>Asunto:</strong> {selectedEmail?.subject}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <Button
              onClick={() => deleteEmail(selectedEmail?.id, false)}
              disabled={deletingEmail}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {deletingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Mover a Papelera
            </Button>
            <Button
              onClick={() => deleteEmail(selectedEmail?.id, true)}
              disabled={deletingEmail}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Eliminar Permanente
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showEmptyTrashConfirm} onOpenChange={setShowEmptyTrashConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700" data-testid="empty-trash-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Vaciar Papelera
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Se eliminarán PERMANENTEMENTE del servidor
              <strong className="text-red-400"> {trashInfo?.count?.toLocaleString() || 0} email{trashInfo?.count !== 1 ? 's' : ''} </strong>
              de la carpeta <strong>{trashInfo?.folder}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" disabled={emptyingTrash}>
              Cancelar
            </AlertDialogCancel>
            <Button
              onClick={handleEmptyTrash}
              disabled={emptyingTrash}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-empty-trash-btn"
            >
              {emptyingTrash ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Vaciar Papelera Definitivamente
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de respuesta personalizada */}
      <Dialog open={showReplyModal} onOpenChange={setShowReplyModal}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="h-5 w-5 text-blue-400" />
              Responder Email
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enviar respuesta a: {emailContent?.from_email || selectedEmail?.from_email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Asunto</label>
              <Input
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="RE: ..."
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Mensaje</label>
              <Textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white min-h-[150px]"
                placeholder="Escribe tu respuesta aquí..."
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReplyModal(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={sendCustomReply}
              disabled={sendingCustomReply || !replyBody.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingCustomReply ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Enviar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Distribuir email al evento (peticiones + docs con IA) */}
      <EmailAIDispatchDialog
        isOpen={aiDispatchOpen}
        onClose={() => setAiDispatchOpen(false)}
        email={selectedEmail}
        onApplied={() => {
          // refrescar datos tras aplicar
          loadStats && loadStats();
        }}
      />

      {/* Dialog: Clasificar documento (Gestión Documental Fase B — proveedores genéricos) */}
      <DocumentClassifyDialog
        open={classifyDialogOpen}
        onOpenChange={setClassifyDialogOpen}
        token={localStorage.getItem('token')}
        emailId={selectedEmail?.id}
        attachment={classifyAttachment}
      />

      {/* Dialog: Clasificar nómina (Gestión Documental Fase C — mb45.com) */}
      <NominaClassifyDialog
        open={nominaDialogOpen}
        onOpenChange={setNominaDialogOpen}
        token={localStorage.getItem('token')}
        emailId={selectedEmail?.id}
        attachment={nominaAttachment}
      />

      {/* Dialog: Clasificar todos los documentos de este email (Gestión Documental Fase E — mb45.com) */}
      <NominaBatchClassifyDialog
        open={nominaBatchDialogOpen}
        onOpenChange={setNominaBatchDialogOpen}
        token={localStorage.getItem('token')}
        emailId={selectedEmail?.id}
      />
    </div>
  );
};

export default EmailHub;
